import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { fadeIn, fadeOut } from 'src/app/animations';
import { Item } from 'src/app/models/item.model';
import { Job } from 'src/app/models/job.model';
import { JobService } from 'src/app/services/sections/job/job.service';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { JobStatusTypes } from 'src/app/models/enum/job-status-types.enum';
import { JobStatus } from 'src/app/models/job-status.model';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';
import { MassCompletionOtherAssets } from 'src/app/models/mass-completion-other-assets.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { PageType } from 'src/app/models/enum/page-type.enum';
import { BitwiseHelper } from 'src/app/services/helpers/bitwise.helper';
import { CompletionDateType } from 'src/app/models/enum/completion-date-type.enum';

@Component({
	templateUrl: './change-status.component.html',
	styleUrls: ['./change-status.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss'],
	animations: [fadeIn(300), fadeOut(300)]
})
export class ChangeStatusComponent implements OnInit, OnDestroy, AfterViewInit {

	@ViewChild('formsWarningModal') public formsWarningModal: GenericModalComponent;

	public completionResults: Item<number, string>[] = [];
	public completionOptions: Item<number, string>[] = [];
	public massCompletionAdditionalAssets: MassCompletionOtherAssets;
	public completedStatusFormModel: FormGroup;

	public self = this;
	public job: Job = null;
	public pageType: PageType;
	public customerPreferences: SettingsPreferences;
	public canCompleteJob = false;

	public isLoading = true;
	public isReasonLoading = false;
	public isStatusLoading = false;

	public readonly ButtonIcons = ButtonIcons;
	public readonly PageType = PageType;
	public readonly JobStatusTypes = JobStatusTypes;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public readonly BitwiseHelper = BitwiseHelper;

	private unsubscriber$ = new Subject<void>();
	public moment = moment;
	public get isActionDisabled() {
		return this.isJobFlowDone || this.completedStatusFormModel?.invalid;
	}

	public get isJobFlowDone() {
		return [JobStatusTypes.Completed, JobStatusTypes.Canceled].includes(this.job.statusId);
	}

	public get showResultOptions() {
		return this.completionResults.length !== 1;
	}

	public get isActionAvailable(): boolean {
		return !this.customerPreferences?.preferences.isJobAutoSkipped &&
			!(this.pageType === PageType.Customer &&
				this.customerPreferences?.preferences.isNotificationDisabled
			);
	}

	@ViewChild('warningModal') completionDateWarningModal: GenericModalComponent;

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private jobService: JobService,
		public dialog: MatDialog
	) { }

	ngOnInit(): void {
		this.job = (this.activatedRoute.snapshot.data.jobDetails || {}) as Job;
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;
		this.canCompleteJob = this.activatedRoute.snapshot.data.canCompleteJob;
		this.pageType = this.activatedRoute.snapshot.data.pageType;

		GlobalSettingsService.isSecondaryLoaderLoading = true;
		this.isLoading = true;

		this.completionOptions = [
			{ id: CompletionDateType.Sched, name: 'As Scheduled' },
			{ id: CompletionDateType.Current, name: 'Today' },
			{ id: CompletionDateType.Other, name: 'Other' }
		];

		forkJoin([
			this.job ? this.jobService.getJobCompletionResultOptions(TokenHelper.getToken(this.activatedRoute)) : of([]),
			this.job ? this.jobService.getOtherAssetsForMassCompletion(TokenHelper.getToken(this.activatedRoute)) : of(null)
		])
			.pipe(
				takeUntil(this.unsubscriber$),
				map(([completionResults, additionalAssets]) => {
					return [completionResults, additionalAssets];
				}),
				tap(([completionResults, additionalAssets]) => {
					this.completionResults = completionResults;
					this.massCompletionAdditionalAssets = additionalAssets;
					this.setupCompleteJobForm();

					GlobalSettingsService.isSecondaryLoaderLoading = false;
					this.isLoading = false;
				}))
			.subscribe();
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public completeJob(): void {

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		if ([JobStatusTypes.Completed, JobStatusTypes.Canceled].includes(this.job.statusId)) {
			GlobalSettingsService.isSecondaryLoaderLoading = false;
			this.router.navigate(['../'], { relativeTo: this.activatedRoute });
			return;
		}

		(this.job.isDataCollectionRequired
			? this.jobService.canCompleteJob(TokenHelper.getToken(this.activatedRoute))
			: of(true)
		)
			.subscribe(result => {
				if (result) {
					const payload = this.getStatusChangePayload();

					this.jobService.completeJob(this.activatedRoute.snapshot.paramMap.get('token'), payload)
						.subscribe(() => {
							GlobalSettingsService.isSecondaryLoaderLoading = false;
							this.job.jobEta = moment(payload.collectedOn).parseZone().format('MM/DD/YYYY');
							this.job.statusId = JobStatusTypes.Completed;
							this.job.status = 'Completed';
							GlobalSettingsService.isJobCompletedStatusVisible = this.job.statusId === JobStatusTypes.Completed;
							this.router.navigate(['../'], { relativeTo: this.activatedRoute });
						}, () => {
							GlobalSettingsService.isSecondaryLoaderLoading = false;
						});
				} else {
					this.formsWarningModal.open();
					this.canCompleteJob = false;
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				}
			});
	}

	private getStatusChangePayload(): JobStatus {
		const completionData = this.completedStatusFormModel?.getRawValue();

		const payload: JobStatus = {
			serial: this.job.serial,
			isEtaChanged: completionData.completionDateType !== CompletionDateType.Sched,
			additionalAssets: []
		};

		const offset = completionData.completionDate.getTimezoneOffset();
		completionData.completionDate = new Date(completionData.completionDate.getTime() - (offset * 60 * 1000));

		Object.assign(
			payload,
			{
				resultId: completionData.completionResult,
				result: this.completionResults.find(item => item.id === completionData.completionResult)?.name,
				collectedOn: completionData.completionDate,
				comment: completionData.comment,
				additionalAssets: completionData.additionalAssets
			}
		);

		return payload;
	}
	public selectedCompletionDateType() {
		const completionDateTypeId = this.completedStatusFormModel.get('completionDateType').value;
		return this.completionOptions.find(x => x.id === completionDateTypeId)?.name;
	}
	public selectedCompletionDate() {
		return this.completedStatusFormModel.get('completionDate').value;
	}
	public onCompletionDateClick(): void {
		const completionDateControl = this.completedStatusFormModel.get('completionDate');

		if (!completionDateControl.enabled) {
			this.completionDateWarningModal.open();
		}
	}
	public onOtherAssetChange(assetState: any, assetId: number): void {
		const additionalAssets = this.completedStatusFormModel.get('additionalAssets').value;
		if (assetState.checked) {
			additionalAssets.push(assetId);
		}
		else {
			const index = additionalAssets.indexOf(assetId, 0);
			additionalAssets.splice(index, 1);
		}

		this.completedStatusFormModel.patchValue({
			additionalAssets
		});
	}
	private setupCompleteJobForm(): void {

		this.completedStatusFormModel = new FormGroup({
			completionDateType: new FormControl(CompletionDateType.Sched, [Validators.required]),
			completionDate: new FormControl({ value: new Date(this.job.jobEta), disabled: true }, [Validators.required]),
			completionResult: new FormControl(this.completionResults.length === 1 ? this.completionResults[0].id : null, [Validators.required]),
			comment: new FormControl(null),
			additionalAssets: new FormControl([])
		});

		this.completedStatusFormModel.get('completionDateType').valueChanges
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((change) => {
				const completionDateTypeControl = this.completedStatusFormModel.get('completionDateType');
				const completionDateControl = this.completedStatusFormModel.get('completionDate');

				if (!completionDateTypeControl.value) {
					completionDateTypeControl.setValue(CompletionDateType.Sched);
				}

				if (completionDateTypeControl.value === CompletionDateType.Sched) {
					completionDateControl.setValue(new Date(this.job.jobEta));
					completionDateControl.disable();
				} else if (completionDateTypeControl.value === CompletionDateType.Current) {
					completionDateControl.setValue(new Date());
					completionDateControl.disable();
				} else if (completionDateTypeControl.value === CompletionDateType.Other) {
					completionDateControl.enable();
				}
			});
	}
}
